import React from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Dropdown } from 'react-toolbox';
import TextArea from 'components/TextArea';
import S from '../../TemplateStyling.scss';
import parseMeasurement, { hasDecimalNotation } from './measurementParsing';

const interiorDoorConfigTypes = [
  { value: 'Slab Only (S)', label: 'Slab Only (S)' },
  { value: 'Pre Hung (PH)', label: 'Pre Hung (PH)' },
  { value: 'Bi-Fold (BF)', label: 'Bi-Fold (BF)' },
  { value: 'Bypass (BP)', label: 'Bypass (BP)' },
  { value: 'Accordion (A)', label: 'Accordion (A)' },
];

const slabOrFullFrame = [
  { value: 'Slab', label: 'Slab' },
  { value: 'Full Frame', label: 'Full Frame' },
];

const PRE_HUNG = 'Pre Hung (PH)';
const SLAB_ONLY = 'Slab Only (S)';

// Pre Hung is always full-frame and Slab Only is always a slab; every other
// configuration (Bi-Fold, Barn Door Sliding, Bypass, Accordion) can be either,
// so it still needs the Slab or Full Frame question.
function needsSlabOrFullFrameChoice(configuration) {
  return configuration && configuration !== PRE_HUNG && configuration !== SLAB_ONLY;
}

function isSlab(measure) {
  if (measure.configuration === SLAB_ONLY) return true;
  if (measure.configuration === PRE_HUNG) return false;
  return measure.slab_or_full_frame === 'Slab';
}

function InteriorDoorMeasures({ measures, onChange }) {
  const handleFieldChange = (idx, field, value) => {
    const updated = measures.map((m, i) =>
      (i === idx ? { ...m, [field]: value } : m),
    );
    onChange(updated);
  };

  const handleConfigurationChange = (idx, value) => {
    const updated = measures.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m, configuration: value };
      if (!needsSlabOrFullFrameChoice(value)) next.slab_or_full_frame = '';
      if (!isSlab(next)) next.slab_thickness = '';
      return next;
    });
    onChange(updated);
  };

  const handleSlabOrFullFrameChange = (idx, value) => {
    const updated = measures.map((m, i) => {
      if (i !== idx) return m;
      const next = { ...m, slab_or_full_frame: value };
      if (!isSlab(next)) next.slab_thickness = '';
      return next;
    });
    onChange(updated);
  };

  const handleNestedChange = (idx, group, subfield, value) => {
    const updated = measures.map((m, i) => {
      if (i === idx) {
        return { ...m, [group]: { ...m[group], [subfield]: value } };
      }
      return m;
    });
    onChange(updated);
  };

  const addRow = () => {
    onChange([
      ...measures,
      {
        openingNumber: '',
        location: '',
        configuration: '',
        slab_or_full_frame: '',
        slab_thickness: '',
        jamb_size: '',
        unitSize: { width: '', height: '' },
        finishedOpening: { width: '', height: '' },
        handing: '',
        notes: '',
      },
    ]);
  };

  const removeRow = (idx) => {
    onChange(measures.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <h6>Interior Doors Measured</h6>
      {measures.map((measure, idx) => (
        <div key={measure.openingNumber || `interiorDoor-${idx}`} style={{ border: '1px solid #eee', marginBottom: 10, padding: 10 }}>
          <div className={S.container}>
            <strong>ALL MEASURES MUST BE IN INCHES</strong>
            <strong>Opening #{idx + 1}</strong>
          </div>
          <Input label="Location" value={measure.location} onChange={v => handleFieldChange(idx, 'location', v)} />
          <Dropdown label="Configuration" source={interiorDoorConfigTypes} value={measure.configuration} onChange={v => handleConfigurationChange(idx, v)} />
          {needsSlabOrFullFrameChoice(measure.configuration) && (
            <Dropdown label="Slab or Full Frame" source={slabOrFullFrame} value={measure.slab_or_full_frame} onChange={v => handleSlabOrFullFrameChange(idx, v)} />
          )}
          {isSlab(measure) && (
            <Input label="Slab Thickness" hint="e.g. 34 1/2" value={measure.slab_thickness} onChange={v => handleFieldChange(idx, 'slab_thickness', v)} />
          )}
          <Input label="Jamb Size" hint="e.g. 34 1/2" value={measure.jamb_size} onChange={v => handleFieldChange(idx, 'jamb_size', v)} />
          <strong>UNIT SIZE</strong>
          <Input label="Width" hint="e.g. 34 1/2" value={measure.unitSize.width} onChange={v => handleNestedChange(idx, 'unitSize', 'width', v)} />
          <Input label="Height" hint="e.g. 34 1/2" value={measure.unitSize.height} onChange={v => handleNestedChange(idx, 'unitSize', 'height', v)} />
          <strong>FINISHED OPENING</strong>
          <Input label="Width" hint="e.g. 34 1/2" value={measure.finishedOpening.width} onChange={v => handleNestedChange(idx, 'finishedOpening', 'width', v)} />
          <Input label="Height" hint="e.g. 34 1/2" value={measure.finishedOpening.height} onChange={v => handleNestedChange(idx, 'finishedOpening', 'height', v)} />
          <strong>OTHER</strong>
          <Input label="Handing" value={measure.handing} onChange={v => handleFieldChange(idx, 'handing', v)} />
          <TextArea
            label="Notes"
            value={measure.notes || ''}
            onChange={e => handleFieldChange(idx, 'notes', e.target.value)}
          />
          <Button className={S.removeButton} label="Remove" onClick={() => removeRow(idx)} />
        </div>
      ))}
      <Button className={S.button} label="Add Interior Door" onClick={addRow} />
    </div>
  );
}

InteriorDoorMeasures.propTypes = {
  measures: PropTypes.arrayOf(
    PropTypes.shape({
      openingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      location: PropTypes.string,
      configuration: PropTypes.string,
      slab_or_full_frame: PropTypes.string,
      slab_thickness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      jamb_size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unitSize: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      finishedOpening: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      handing: PropTypes.string,
      notes: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default InteriorDoorMeasures;

export function validateInteriorDoorMeasures(measures) {
  const errors = {};
  measures.forEach((item, idx) => {
    if (needsSlabOrFullFrameChoice(item.configuration) && !item.slab_or_full_frame) {
      if (!errors[idx]) errors[idx] = {};
      errors[idx].slab_or_full_frame = ['slab_or_full_frame is required for this configuration.'];
    }
    if (isSlab(item) && (item.slab_thickness === undefined || item.slab_thickness === '')) {
      if (!errors[idx]) errors[idx] = {};
      errors[idx].slab_thickness = ['slab_thickness is required for a slab configuration.'];
    }
    const measurementFields = [
      'slab_thickness',
      'jamb_size',
    ];
    measurementFields.forEach((field) => {
      const value = item[field];
      if (value !== undefined && value !== '' && isNaN(parseMeasurement(value))) {
        if (!errors[idx]) errors[idx] = {};
        errors[idx][field] = [`${field} must be a number or fraction.`];
      }
    });
    if (item.unitSize) {
      ['width', 'height'].forEach((subfield) => {
        const value = item.unitSize[subfield];
        if (value === undefined || value === '') return;
        if (isNaN(parseMeasurement(value))) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`unitSize.${subfield}`] = [`unitSize.${subfield} must be a number or fraction.`];
        } else if (hasDecimalNotation(value)) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`unitSize.${subfield}`] = [`unitSize.${subfield} must not use decimal notation; use a fraction instead (e.g. 34 1/2).`];
        }
      });
    }
    if (item.finishedOpening) {
      ['width', 'height'].forEach((subfield) => {
        const value = item.finishedOpening[subfield];
        if (value === undefined || value === '') return;
        if (isNaN(parseMeasurement(value))) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`finishedOpening.${subfield}`] = [`finishedOpening.${subfield} must be a number or fraction.`];
        } else if (hasDecimalNotation(value)) {
          if (!errors[idx]) errors[idx] = {};
          errors[idx][`finishedOpening.${subfield}`] = [`finishedOpening.${subfield} must not use decimal notation; use a fraction instead (e.g. 34 1/2).`];
        }
      });
    }
  });
  if (Object.keys(errors).length > 0) {
    errors.interiorDoorMeasures = [
      'Please ensure all numeric fields are valid numbers.',
    ];
  }
  return errors;
}



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/ifiTemplate/templates/components/measures/InteriorDoorMeasures.jsx